<template>
    <div class="compare-text-render pa-shrink-0">
        <ElPopover
            :visibleArrow="false"
            :openDelay="200"
            :disabled="!compareBtnDisabled"
            trigger="hover"
            placement="top-end"
            popperClass="compare-wording-tips__popover"
        >
            <div slot="reference">
                <button
                    class="th-btn th-btn-text"
                    :disabled="compareBtnDisabled"
                    @click="onShowComparePatentText"
                >
                    {{ t('patent-view.compareView.textCompare') }}
                </button>
            </div>
            <p class="pa-max-w-[200px]">
                <span>{{ t('patent-view.compareView.textCompareTips') }}</span>
            </p>
        </ElPopover>
        <ElDialog
            v-if="showDialog"
            :visible="true"
            width="960px"
            :title="t('patent-view.compareView.textCompare')"
            :appendToBody="true"
            :closeOnClickModal="false"
            :closeOnPressEscape="false"
            class="compare-text-dialog"
            @close="showDialog = false;"
        >
            <div class="compare-text-wrapper pa-flex pa-border pa-border-gray-40 pa-h-[496px] pa-w-full pa-rounded">
                <div class="pa-flex-1 pa-shrink-0 pa-flex pa-flex-col pa-overflow-hidden pa-border-r pa-border-gray-40">
                    <div class="pa-px-2 pa-py-1 pa-shrink-0 pa-leading-6 pa-flex pa-items-center">
                        <span class="pa-text-green-600 pa-shrink-0">{{ leftPatent.PN }}</span>
                        <span class="pa-flex-1 pa-ml-1 pa-truncate" v-html="leftPatent.Origin_TITLE" />
                    </div>
                    <div class="pa-h-[36px] pa-bg-gray-20 pa-shrink-0">
                        <MenuTabsScrollable
                            ref="menuTabsScrollable"
                            :currentTab="currentTab"
                            :defaultTabs="defaultTabs"
                            :showTooltip="true"
                            @switchTabHanlder="switchTabHandler"
                        />
                    </div>
                    <div class="pa-overflow-auto pa-p-4 pa-flex-1">
                        <CompareLoadingContent
                            :key="`${currentTab}__added`"
                            pattern="added"
                            :textType="currentTab"
                            @loaded="(status) => { compareAddedLoaded = status; }"
                        />
                    </div>
                </div>
                <div class="pa-flex-1 pa-shrink-0 pa-flex pa-flex-col pa-overflow-hidden">
                    <div class="pa-px-2 pa-py-1 pa-shrink-0 pa-leading-6 pa-flex pa-items-center">
                        <span class="pa-text-green-600 pa-shrink-0">{{ rightPatent.PN }}</span>
                        <span class="pa-flex-1 pa-ml-1 pa-truncate" v-html="rightPatent.Origin_TITLE" />
                    </div>
                    <div class="pa-h-[40px] pa-bg-gray-20 pa-shrink-0" />
                    <div class="pa-overflow-auto pa-p-4 pa-flex-1">
                        <CompareLoadingContent
                            :key="`${currentTab}__removed`"
                            pattern="removed"
                            :textType="currentTab"
                            @loaded="(status) => { compareRemoveLoaded = status; }"
                        />
                    </div>
                </div>
            </div>
        </ElDialog>
    </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { Dialog, Popover } from 'element-ui';
import MenuTabsScrollable from 'library-@/components/MenuTabs/MenuTabsScrollable';
import CompareLoadingContent from './CompareTextContent.vue';
import BasicService from '@/services/basic-service';
import { useCompareInit } from '@/composables/useCompareInit';
import { useCompareUrlState } from '@/composables/useCompareUrlState';
import { useI18n } from '@/composables/useI18n';
import { useRoute } from 'vue-router/composables';

import _ from 'lodash';

interface Patent {
    PN?: string;
    Origin_TITLE?: string;
}

const ElDialog = Dialog;
const ElPopover = Popover;

const { t } = useI18n();
const route = useRoute();
const { leftId, rightId } = useCompareUrlState();
const { textCompareLang } = useCompareInit();

const showDialog = ref(false);

// 假的tab
const tabsLabel = {
    clms: t('patent-view.CLMS'),
    desc: t('patent-view.DESC'),
}

const hasTextCompareLangTab = computed(() => {
    return _.pickBy(textCompareLang.value, item => item)
})

const defaultTabs = computed(() => {
    const tabList = Object.keys(hasTextCompareLangTab.value || {}).sort();
    return tabList.map(item => {
        return {
            label: tabsLabel[item],
            key: item,
            hasAcl: true
        }
    });
});

const currentTab = ref('');

const setDefaultTab = () => {
    const curTab = route.params.left;
    if (_.get(hasTextCompareLangTab.value, curTab)) {
        currentTab.value = curTab;
    } else {
        currentTab.value = _.get(defaultTabs.value[0], 'key', 'clms');
    }
}

const compareAddedLoaded = ref(false);
const compareRemoveLoaded = ref(false);

const isCompareTextLoaded = computed(() => {
    return compareAddedLoaded.value && compareRemoveLoaded.value;
})

const switchTabHandler = (item) => {
    if (!isCompareTextLoaded.value) return;
    currentTab.value = item.key;
}

const leftPatent = ref<Patent>({});
const rightPatent = ref<Patent>({})
const comparePatentTextInit = async () => {
    const leftBasicService = new BasicService(leftId.value);
    const rightBasicService = new BasicService(rightId.value);
    Promise.all([leftBasicService.getPatentBase(), rightBasicService.getPatentBase()])
        .then(([left, right]) => {
            leftPatent.value = left;
            rightPatent.value = right;
        });
}
const onShowComparePatentText = async () => {
    setDefaultTab();
    showDialog.value = true;
    await comparePatentTextInit();
}

const compareBtnDisabled = computed(() => {    
    return !textCompareLang.value || _.isEmpty(hasTextCompareLangTab.value)
})

</script>

<style lang="scss">
.compare-text-dialog.el-dialog__wrapper {
    .el-dialog__header {
        @apply pa-px-6 pa-py-5;
        .el-dialog__headerbtn {
            @apply pa-top-5 pa-right-6;
        }
    }
    .el-dialog__body {
        @apply pa-pt-0 pa-pr-6 pa-pb-[40px] pa-pl-6 pa-text-gray-1100;
    }
}
</style>
